import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Kanban,
  List,
  Search,
  GripVertical,
  User,
  Mail,
  Phone,
  Building2
} from 'lucide-react';

const PersonnelCard = ({ person, category, isDragging }) => (
  <div
    className={`kanban-card ${isDragging ? 'opacity-50' : ''}`}
    data-testid={`personnel-card-${person.id}`}
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <span className="text-primary font-semibold text-sm">
          {person.name.charAt(0)}{person.surname.charAt(0)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{person.name} {person.surname}</h4>
        <p className="text-sm text-muted-foreground truncate">{person.position || 'No position'}</p>
      </div>
      <GripVertical className="w-4 h-4 text-muted-foreground/50 flex-shrink-0 cursor-grab" />
    </div>
    {person.department && (
      <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
        <Building2 className="w-3 h-3" />
        {person.department}
      </div>
    )}
  </div>
);

const SortableCard = ({ person, category }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: person.id, data: { person, categoryId: category?.id } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <PersonnelCard person={person} category={category} isDragging={isDragging} />
    </div>
  );
};

const KanbanColumn = ({ category, personnel }) => {
  const filteredPersonnel = personnel.filter(p => 
    category ? p.category_id === category.id : !p.category_id
  );

  return (
    <div className="kanban-column" data-testid={`kanban-column-${category?.id || 'unassigned'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: category?.color || '#6B7280' }}
          />
          <h3 className="font-semibold">{category?.name || 'Unassigned'}</h3>
        </div>
        <Badge variant="secondary" className="text-xs">
          {filteredPersonnel.length}
        </Badge>
      </div>
      
      <SortableContext
        items={filteredPersonnel.map(p => p.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3 min-h-[200px]">
          {filteredPersonnel.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm text-muted-foreground border-2 border-dashed border-border rounded-lg">
              Drop personnel here
            </div>
          ) : (
            filteredPersonnel.map((person) => (
              <SortableCard key={person.id} person={person} category={category} />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
};

export const PersonnelTracking = () => {
  const { api } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState('kanban');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchData = async () => {
    try {
      const [persRes, catRes] = await Promise.all([
        api.get('/personnel', { params: { is_active: true } }),
        api.get('/categories')
      ]);
      setPersonnel(persRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activePersonId = active.id;
    const overData = over.data.current;
    
    // Find the target category
    let targetCategoryId = null;
    
    if (overData?.person) {
      // Dropped on another person card
      targetCategoryId = overData.categoryId;
    } else if (over.id.toString().startsWith('column-')) {
      // Dropped on a column
      targetCategoryId = over.id.toString().replace('column-', '');
      if (targetCategoryId === 'unassigned') targetCategoryId = null;
    } else {
      // Check if it's a category column
      const category = categories.find(c => c.id === over.id);
      if (category) {
        targetCategoryId = category.id;
      } else if (over.id === 'unassigned') {
        targetCategoryId = null;
      }
    }

    const activePerson = personnel.find(p => p.id === activePersonId);
    if (!activePerson || activePerson.category_id === targetCategoryId) return;

    // Optimistic update
    setPersonnel(prev => prev.map(p => 
      p.id === activePersonId ? { ...p, category_id: targetCategoryId } : p
    ));

    try {
      await api.put(`/personnel/${activePersonId}/category/${targetCategoryId || 'none'}`);
      toast.success('Personnel moved successfully');
    } catch (error) {
      // Revert on error
      setPersonnel(prev => prev.map(p => 
        p.id === activePersonId ? { ...p, category_id: activePerson.category_id } : p
      ));
      toast.error('Failed to move personnel');
    }
  };

  const filteredPersonnel = personnel.filter(p =>
    search === '' ||
    `${p.name} ${p.surname}`.toLowerCase().includes(search.toLowerCase()) ||
    p.national_id.includes(search)
  );

  const activePerson = activeId ? personnel.find(p => p.id === activeId) : null;

  if (loading) {
    return (
      <div className="page-transition space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-4 overflow-x-auto">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-96 w-72 flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Personnel Tracking</h1>
          <p className="text-muted-foreground">
            Track and manage personnel across workflow stages
          </p>
        </div>
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="kanban" className="gap-2" data-testid="kanban-view-btn">
              <Kanban className="w-4 h-4" />
              Kanban
            </TabsTrigger>
            <TabsTrigger value="list" className="gap-2" data-testid="list-view-btn">
              <List className="w-4 h-4" />
              List
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search personnel..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="tracking-search"
        />
      </div>

      {/* Views */}
      {view === 'kanban' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <ScrollArea className="w-full">
            <div className="flex gap-4 pb-4">
              {/* Unassigned Column */}
              <KanbanColumn
                category={null}
                personnel={filteredPersonnel}
              />
              
              {/* Category Columns */}
              {categories.map((category) => (
                <KanbanColumn
                  key={category.id}
                  category={category}
                  personnel={filteredPersonnel}
                />
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <DragOverlay>
            {activePerson && (
              <div className="drag-overlay">
                <PersonnelCard person={activePerson} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {filteredPersonnel.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No personnel found
                </div>
              ) : (
                filteredPersonnel.map((person) => {
                  const category = categories.find(c => c.id === person.category_id);
                  return (
                    <div
                      key={person.id}
                      className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                      data-testid={`list-item-${person.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold text-sm">
                            {person.name.charAt(0)}{person.surname.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium">{person.name} {person.surname}</h4>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {person.position && <span>{person.position}</span>}
                            {person.department && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {person.department}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        style={{ borderColor: category?.color || '#6B7280' }}
                      >
                        <div
                          className="w-2 h-2 rounded-full mr-1.5"
                          style={{ backgroundColor: category?.color || '#6B7280' }}
                        />
                        {category?.name || 'Unassigned'}
                      </Badge>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PersonnelTracking;
